// <product-share-nav>: positions the Share popover under its button and handles the
// --copy (copy product link) and --native (system share sheet) commands.
class ProductShareNav extends HTMLElement {
  connectedCallback() {
    this.menu = this.querySelector('#psn-menu');
    this.trigger = this.querySelector('[commandfor="psn-menu"]');
    if (!this.menu || !this.trigger) return;
    if (navigator.share) this.menu.classList.add('has-native');

    this.menu.addEventListener('beforetoggle', (event) => {
      if (event.newState !== 'open') return;
      this.menu.classList.remove('is-copied');
      this.trigger.setAttribute('aria-expanded', 'true');
      this.place();
    });
    this.menu.addEventListener('toggle', (event) => {
      if (event.newState === 'closed') this.trigger.setAttribute('aria-expanded', 'false');
    });
    this.menu.addEventListener('click', (event) => {
      if (event.target.closest('a')) this.menu.hidePopover();
    });
    this.addEventListener('command', (event) => this.onCommand(event));
    this.addEventListener('click', (event) => this.openWhatsApp(event));
    const follow = () => this.menu.matches(':popover-open') && this.place();
    window.addEventListener('resize', follow, { passive: true });
    window.addEventListener('scroll', follow, { passive: true });
  }

  place() {
    const rect = this.trigger.getBoundingClientRect();
    // The menu follows its button while the page scrolls and closes once the button leaves the screen.
    if (rect.bottom < 0 || rect.top > window.innerHeight) {
      this.menu.hidePopover();
      return;
    }
    const width = Math.min(260, window.innerWidth - 16);
    const isRtl = document.documentElement.dir === 'rtl';
    let left = isRtl ? rect.left : rect.right - width;
    left = Math.max(8, Math.min(left, window.innerWidth - width - 8));
    this.menu.style.width = `${width}px`;
    this.menu.style.left = `${left}px`;
    this.menu.style.top = `${rect.bottom + 6}px`;
  }

  // WhatsApp links open the WhatsApp app directly. Phones and tablets use the whatsapp:// app link; if the
  // app does not open within ~1.6s (not installed, or an in-app browser blocks it) the normal wa.me link
  // loads instead. Computers open WhatsApp Web straight to the chat, skipping the wa.me landing page.
  openWhatsApp(event) {
    const link = event.target.closest('[data-psn-wa]');
    if (!link || event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey) return;
    const text = encodeURIComponent(link.dataset.waText || '');
    const phone = link.dataset.phone || '';
    const query = `${phone ? `phone=${phone}&` : ''}text=${text}`;
    const isMobile = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent) || (navigator.maxTouchPoints > 1 && /Macintosh/.test(navigator.userAgent));
    event.preventDefault();
    this.menu.hidePopover?.();

    if (!isMobile) {
      window.open(`https://web.whatsapp.com/send?${query}`, '_blank', 'noopener');
      return;
    }

    let left = false;
    const onHide = () => { if (document.hidden) left = true; };
    document.addEventListener('visibilitychange', onHide);
    const onLeave = () => { left = true; };
    window.addEventListener('pagehide', onLeave);
    setTimeout(() => {
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', onLeave);
      if (!left && !document.hidden) window.location.href = link.href;
    }, 1600);
    window.location.href = `whatsapp://send?${query}`;
  }

  async onCommand(event) {
    const url = this.dataset.url;
    if (event.command === '--copy') {
      try {
        await navigator.clipboard.writeText(url);
      } catch {
        const field = document.createElement('textarea');
        field.value = url;
        document.body.append(field);
        field.select();
        document.execCommand('copy');
        field.remove();
      }
      this.menu.classList.add('is-copied');
      setTimeout(() => this.menu.hidePopover(), 1200);
    }
    if (event.command === '--native' && navigator.share) {
      this.menu.hidePopover();
      try {
        await navigator.share({ title: this.dataset.title, text: this.dataset.text, url });
      } catch {
        /* Shopper closed the share sheet. */
      }
    }
  }
}

if (!customElements.get('product-share-nav')) customElements.define('product-share-nav', ProductShareNav);
